import React, {useMemo} from 'react';

import cs from 'classnames';
import {CSSTransition} from 'react-transition-group';
import styled from 'styled-components';

import {MultipleAssignedAvatar} from 'app/components/general/avatars/MultipleAssignedAvatar';
import {gt} from 'app/utils/i18n';

import {AssignUsersField} from '../AssignUsersField';

import type {AssigneesByType, AssigneeType} from '../types';
import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  RegularProcessProcedureAsStepSerializedType,
  RoleSerializedType,
  TeamSerializedType,
  UserSerializedType,
} from 'sweet-types';

export type AssignableStepType =
  | ProcedureContentStepType
  | ProcedureDecisionStepType
  | RegularProcessProcedureAsStepSerializedType;

interface Props {
  icon: React.ReactNode;
  title: React.ReactNode;
  postTitle?: React.ReactNode;
  assignees?: AssigneesByType;
  immutableAssignees?: AssigneesByType;
  assigning: boolean;
  setAssigning: (assigning: boolean) => unknown;
  onChange: (
    meta: ActionMeta<AssigneeType>,
    ctname: 'user' | 'team' | 'role'
  ) => unknown;
  footer?: React.ReactNode;
}

export function AssignableItemStructure({
  icon,
  title,
  postTitle,
  assignees,
  immutableAssignees,
  assigning,
  setAssigning,
  onChange,
  footer,
}: Props) {
  const avatarAssignees: Array<
    UserSerializedType | TeamSerializedType | RoleSerializedType
  > = useMemo(() => {
    if (assignees == null) {
      assignees = {users: [], teams: [], roles: []};
    }
    if (immutableAssignees == null) {
      immutableAssignees = {users: [], teams: [], roles: []};
    }

    return [
      ...(assignees.users ?? []),
      ...(assignees.teams ?? []),
      ...(assignees.roles ?? []),
      ...(immutableAssignees.users ?? []),
      ...(immutableAssignees.teams ?? []),
      ...(immutableAssignees.roles ?? []),
    ] as Array<UserSerializedType | TeamSerializedType | RoleSerializedType>;
  }, [assignees, immutableAssignees]);

  return (
    <StyledItemDiv className={cs('list-group-item p-0', {assigning})}>
      <button
        type="button"
        className="list-group-item list-group-item-action d-flex title align-items-center"
        onClick={() => setAssigning(!assigning)}
      >
        <div style={{width: 30}} className="flex-shrink-0 flex-grow-0">
          {icon}
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 style={{fontSize: '1.1rem'}} className="mb-0">
                {title}
              </h5>
              {postTitle}
            </div>
            {avatarAssignees.length ? (
              <div className="flex-grow-0 ml-1">
                <MultipleAssignedAvatar
                  usersAndTeamsAndRoles={avatarAssignees}
                  tooltipTitle={gt('Assigned to')}
                />
              </div>
            ) : !assigning ? (
              <span className="btn btn-light btn-sm ml-2 assign">
                {gt('Assign')}
              </span>
            ) : null}
          </div>
          {footer}
        </div>
      </button>
      <CSSTransition
        appear={true}
        in={assigning}
        timeout={120}
        classNames="assign-controls"
        unmountOnExit
      >
        <div>
          <div className="px-2 d-flex" style={{paddingBottom: '0.75rem'}}>
            <div style={{width: 30}} className="flex-shrink-0 flex-grow-0" />
            <div className="flex-grow-1">
              <AssignUsersField assignments={assignees} onChange={onChange} />
              <div className="mt-2 justify-content-end d-flex">
                <button
                  className="btn btn-sm btn-light px-3"
                  type="button"
                  onClick={() => setAssigning(false)}
                >
                  {gt('Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </CSSTransition>
    </StyledItemDiv>
  );
}

const StyledItemDiv = styled.div`
  transition: background-color 150ms;

  &.assigning {
    background-color: #f8f9fa;

    button.title {
      background-color: #f8f9fa;
    }
  }

  .assign-controls-enter {
    max-height: 1px;
    overflow: hidden;
  }
  .assign-controls-enter-active {
    max-height: 120px;
    transition: max-height 150ms;
    overflow: hidden;
  }
  .assign-controls-exit {
    max-height: 120px;
    overflow: hidden;
  }
  .assign-controls-exit-active {
    max-height: 1px;
    transition: max-height 150ms;
    overflow: hidden;
  }

  button.title {
    border: none;
    padding: 0.75rem 0.5rem;
  }

  .btn.assign {
    background: white;
  }
`;
